import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy, RefreshCw, Save } from 'lucide-react'
import { type FormEvent, useEffect, useId, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

import type { DriverRow } from './driver-types'

/**
 * Driver create / edit dialog.
 *
 * The two modes are SHAPED DIFFERENTLY because the API treats
 * them differently:
 *
 *   - **Create** → `POST /auth/register` with name + email +
 *     password + role. The dialog includes a "Générer" button
 *     that mints a random 12-char password the admin can copy
 *     and forward to the driver.
 *   - **Edit** → `PATCH /api/users/:id` with name / email /
 *     active. Role and password are out of scope (see API note
 *     in `users.schema.ts.updateUserSchema`).
 *
 * We intentionally render TWO different bodies rather than one
 * with conditional fields. The admin's mental model is "I'm
 * creating a new account" vs "I'm fixing this row" — keeping
 * the layouts distinct prevents the field-disabled-because-
 * edit-mode dance that confuses end users.
 */

interface DriverDialogProps {
  driver: DriverRow | null | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u

export function DriverDialog({ driver, open, onOpenChange }: DriverDialogProps) {
  const isEdit = driver !== undefined && driver !== null

  if (driver === undefined) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        {isEdit ? (
          <EditForm driver={driver} onClose={() => onOpenChange(false)} />
        ) : (
          <CreateForm onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/*  Create — POST /auth/register                                               */
/* -------------------------------------------------------------------------- */

interface CreateFormState {
  name: string
  email: string
  password: string
  role: 'admin' | 'driver'
}

interface CreateFormErrors {
  name?: string
  email?: string
  password?: string
}

const EMPTY_CREATE: CreateFormState = {
  name: '',
  email: '',
  password: '',
  role: 'driver',
}

function CreateForm({ onClose }: { onClose: () => void }) {
  const api = useApi()
  const queryClient = useQueryClient()
  const titleId = useId()
  const nameId = useId()
  const emailId = useId()
  const passwordId = useId()
  const roleId = useId()

  const [form, setForm] = useState<CreateFormState>(EMPTY_CREATE)
  const [errors, setErrors] = useState<CreateFormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [copyHint, setCopyHint] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      await api.post<unknown>('/auth/register', {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] })
      onClose()
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "Impossible de créer le compte.",
      )
    },
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const validation = validateCreate(form)
    setErrors(validation)
    if (Object.keys(validation).length > 0) return
    setSubmitError(null)
    createMutation.mutate()
  }

  function generatePassword(): void {
    const generated = generateRandomPassword(12)
    setForm({ ...form, password: generated })
    setCopyHint(null)
  }

  async function copyPassword(): Promise<void> {
    if (form.password === '') return
    try {
      await navigator.clipboard.writeText(form.password)
      setCopyHint('Mot de passe copié !')
      window.setTimeout(() => setCopyHint(null), 2000)
    } catch {
      setCopyHint('Copie impossible — sélectionnez et copiez manuellement.')
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle id={titleId}>Nouveau compte</DialogTitle>
        <DialogDescription>
          Créez un compte chauffeur ou administrateur. Communiquez le
          mot de passe à la personne via un canal sécurisé.
        </DialogDescription>
      </DialogHeader>

      <DialogBody className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nom *" error={errors.name} wide>
          <Input
            id={nameId}
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            invalid={errors.name !== undefined}
            placeholder="Prénom Nom"
            autoComplete="name"
          />
        </Field>

        <Field label="Email *" error={errors.email}>
          <Input
            id={emailId}
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            invalid={errors.email !== undefined}
            placeholder="prenom.nom@exemple.fr"
            autoComplete="off"
          />
        </Field>

        <Field label="Rôle">
          <select
            id={roleId}
            value={form.role}
            onChange={(e) =>
              setForm({ ...form, role: e.target.value as 'admin' | 'driver' })
            }
            className={cn(
              'h-10 w-full rounded-md border border-surface-border bg-surface px-3 text-sm',
              'focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand',
            )}
          >
            <option value="driver">Chauffeur</option>
            <option value="admin">Administrateur</option>
          </select>
        </Field>

        <div className="sm:col-span-2 flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor={passwordId}>Mot de passe *</Label>
            <button
              type="button"
              onClick={generatePassword}
              className="text-xs text-brand hover:underline inline-flex items-center gap-1"
            >
              <RefreshCw size={12} aria-hidden="true" />
              Générer
            </button>
          </div>
          <div className="flex gap-2">
            <Input
              id={passwordId}
              type="text"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              invalid={errors.password !== undefined}
              placeholder="Minimum 8 caractères"
              autoComplete="off"
              className="font-mono"
            />
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={() => void copyPassword()}
              disabled={form.password === ''}
              aria-label="Copier le mot de passe"
            >
              <Copy size={14} />
            </Button>
          </div>
          {errors.password !== undefined ? (
            <p className="text-xs text-danger">{errors.password}</p>
          ) : (
            <p className="text-xs text-text-muted">
              {copyHint ??
                'Affiché en clair pour transmission à la personne. Ne sera plus visible ensuite.'}
            </p>
          )}
        </div>

        {submitError !== null ? (
          <p
            role="alert"
            className="sm:col-span-2 text-sm text-danger text-center font-medium"
          >
            {submitError}
          </p>
        ) : null}
      </DialogBody>

      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Annuler
          </Button>
        </DialogClose>
        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending ? (
            <Spinner label="Création" />
          ) : (
            <>
              <Save size={16} />
              Créer le compte
            </>
          )}
        </Button>
      </DialogFooter>
    </form>
  )
}

function validateCreate(form: CreateFormState): CreateFormErrors {
  const errors: CreateFormErrors = {}
  if (form.name.trim() === '') errors.name = 'Le nom est obligatoire.'
  if (form.email.trim() === '') errors.email = "L'email est obligatoire."
  else if (!EMAIL_REGEX.test(form.email)) errors.email = 'Email invalide.'
  if (form.password.length < 8) {
    errors.password = 'Minimum 8 caractères.'
  }
  return errors
}

/**
 * Generate a 12-character password from a 64-char alphabet
 * containing letters (mixed case), digits, and a small set of
 * separators that are easy to read aloud (`-`, `_`, `.`).
 *
 * Uses Web Crypto so each character is uniformly distributed —
 * avoids the bias of `Math.random() * alphabet.length`.
 */
function generateRandomPassword(length: number): string {
  const alphabet =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789-_.'
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  let out = ''
  for (let i = 0; i < length; i += 1) {
    out += alphabet[bytes[i]! % alphabet.length]
  }
  return out
}

/* -------------------------------------------------------------------------- */
/*  Edit — PATCH /api/users/:id                                                */
/* -------------------------------------------------------------------------- */

interface EditFormState {
  name: string
  email: string
  active: boolean
}

interface EditFormErrors {
  name?: string
  email?: string
}

function EditForm({
  driver,
  onClose,
}: {
  driver: DriverRow
  onClose: () => void
}) {
  const api = useApi()
  const queryClient = useQueryClient()
  const titleId = useId()
  const nameId = useId()
  const emailId = useId()

  const [form, setForm] = useState<EditFormState>({
    name: driver.name,
    email: driver.email,
    active: driver.active,
  })
  const [errors, setErrors] = useState<EditFormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Re-sync the form whenever the driver prop changes (the
  // dialog stays mounted between rows opened via "Modifier").
  useEffect(() => {
    setForm({
      name: driver.name,
      email: driver.email,
      active: driver.active,
    })
    setErrors({})
    setSubmitError(null)
  }, [driver])

  const editMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      // Only send changed fields. Avoids sending the same
      // email back, which would still pass but is needless
      // noise.
      const payload: { name?: string; email?: string; active?: boolean } = {}
      if (form.name.trim() !== driver.name) payload.name = form.name.trim()
      if (form.email.trim().toLowerCase() !== driver.email) {
        payload.email = form.email.trim().toLowerCase()
      }
      if (form.active !== driver.active) payload.active = form.active
      await api.patch<unknown>(`/api/users/${driver.id}`, payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] })
      onClose()
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "Impossible d'enregistrer les modifications.",
      )
    },
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const validation = validateEdit(form)
    setErrors(validation)
    if (Object.keys(validation).length > 0) return
    setSubmitError(null)
    editMutation.mutate()
  }

  return (
    <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle id={titleId}>Modifier le compte</DialogTitle>
        <DialogDescription>
          Le rôle et le mot de passe ne sont pas modifiables ici (actions
          dédiées à venir).
        </DialogDescription>
      </DialogHeader>

      <DialogBody className="grid grid-cols-1 gap-4">
        <Field label="Nom" error={errors.name}>
          <Input
            id={nameId}
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            invalid={errors.name !== undefined}
          />
        </Field>

        <Field label="Email" error={errors.email}>
          <Input
            id={emailId}
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            invalid={errors.email !== undefined}
          />
        </Field>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) =>
              setForm({ ...form, active: e.target.checked })
            }
            className="size-4 rounded border-2 border-surface-border text-brand"
          />
          <span className="text-sm">
            Compte actif (peut se connecter et apparaître dans les
            sélecteurs)
          </span>
        </label>

        {submitError !== null ? (
          <p
            role="alert"
            className="text-sm text-danger text-center font-medium"
          >
            {submitError}
          </p>
        ) : null}
      </DialogBody>

      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Annuler
          </Button>
        </DialogClose>
        <Button type="submit" disabled={editMutation.isPending}>
          {editMutation.isPending ? (
            <Spinner label="Enregistrement" />
          ) : (
            <>
              <Save size={16} />
              Enregistrer
            </>
          )}
        </Button>
      </DialogFooter>
    </form>
  )
}

function validateEdit(form: EditFormState): EditFormErrors {
  const errors: EditFormErrors = {}
  if (form.name.trim() === '') errors.name = 'Le nom est obligatoire.'
  if (form.email.trim() === '') errors.email = "L'email est obligatoire."
  else if (!EMAIL_REGEX.test(form.email)) errors.email = 'Email invalide.'
  return errors
}

/* -------------------------------------------------------------------------- */
/*  Field helper                                                               */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  error,
  wide = false,
  children,
}: {
  label: string
  /** Allow `undefined` so callers can pass optional state values directly. */
  error?: string | undefined
  wide?: boolean
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', wide ? 'sm:col-span-2' : '')}>
      <Label>{label}</Label>
      {children}
      {error !== undefined ? (
        <p className="text-xs text-danger">{error}</p>
      ) : null}
    </div>
  )
}
